import { useEffect, useRef, useCallback, useState } from 'react';

/**
 * Hook that auto-refreshes data on:
 * 1. Initial mount
 * 2. Interval polling (default 30s)
 * 3. Window focus (tab becomes visible again)
 * 4. Manual trigger
 */
export function useAutoRefresh(fetchFn, intervalMs = 30000) {
  const [lastUpdated, setLastUpdated] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const intervalRef = useRef(null);
  const fetchRef = useRef(fetchFn);

  // Keep ref current
  fetchRef.current = fetchFn;

  const refresh = useCallback(async (showIndicator = false) => {
    if (showIndicator) setRefreshing(true);
    try {
      await fetchRef.current();
      setLastUpdated(new Date());
    } catch (e) {
      console.error('Auto-refresh failed:', e);
    } finally {
      setRefreshing(false);
    }
  }, []);

  // Initial load + polling
  useEffect(() => {
    refresh();

    intervalRef.current = setInterval(() => refresh(false), intervalMs);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [refresh, intervalMs]);

  // Refetch on window focus / visibility change
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        refresh(false);
      }
    };

    const handleFocus = () => {
      refresh(false);
    };

    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('focus', handleFocus);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('focus', handleFocus);
    };
  }, [refresh]);

  return { lastUpdated, refreshing, refresh };
}
